import { useCallback, useEffect, useState } from 'react';
import type { CifrasIndicador } from '../../../api/ops/reservas';
import { Chip } from '../../../componentes/Chip';
import { EstadoCargando, EstadoError } from '../../../componentes/EstadoPagina';
import { Icono } from '../../../componentes/Icono';
import { TarjetaCifra } from '../../../componentes/TarjetaCifra';
import { estadoDeError } from '../../../logica/estados';
import { useCabecera } from '../../../marco/cabecera';
import type { PropsPantalla } from '../../tipos';
import type { ContextoReservas } from '../contexto';
import { hoyEn, horaEn } from '../fechas';
import { AYUDA } from '../textos';
import { NavegadorDia } from './NavegadorDia';

/**
 * 8.1 para el Socio (G465): solo cifras agregadas, sin datos de clientes, de `queryIndicator` con el indicador de
 * ocupación de Reservas (`reservas.ocupacion`, ADR-0133) del día elegido y su desglose por zona. Las cifras (porcentaje,
 * plazas reservadas y abiertas) son las de la API; el panel no las calcula.
 */
export const INDICADOR_OCUPACION = 'reservas.ocupacion';

interface Carga {
    dia: string;
    total: CifrasIndicador | null;
    zonas: CifrasIndicador | null;
    error: unknown;
}

const porcentaje = (v: number | null | undefined) => (v == null ? '—' : `${v.toLocaleString('es-ES', { maximumFractionDigits: 0 })} %`);

export function CifrasSocio(props: PropsPantalla & { contexto: ContextoReservas }) {
    const { ops, negocio, movil, contexto } = props;
    const hoy = hoyEn(contexto.zona);
    const [dia, setDia] = useState(hoy);
    const [carga, setCarga] = useState<Carga | null>(null);
    const [intento, setIntento] = useState(0);
    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);
    useCabecera({ ayuda: AYUDA.socio, acciones: [] });

    useEffect(() => {
        let vivo = true;
        Promise.all([
            ops.queryIndicator(negocio, INDICADOR_OCUPACION, { from: dia, to: dia, granularity: 'day' }),
            ops.queryIndicator(negocio, INDICADOR_OCUPACION, { from: dia, to: dia, granularity: 'day', breakdown: 'zone' }).catch(() => null),
        ])
            .then(([total, zonas]) => {
                if (vivo) {
                    setCarga({ dia, total, zonas, error: null });
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setCarga({ dia, total: null, zonas: null, error: e });
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, dia, intento]);

    const vista = carga?.dia === dia ? carga : null;
    const total = vista?.total?.series.find((s) => s.dimension_value == null) ?? vista?.total?.series[0];
    const punto = total?.total;
    const porZona = (vista?.zonas?.series ?? []).filter((s) => s.dimension_value != null);

    return (
        <div className="rv-rs-apartado">
            <div className="rv-rs-dia">
                <NavegadorDia valor={dia} hoy={hoy} movil={movil} onCambio={setDia} />
                {dia === hoy && !movil && <Chip estado="live">{`Ahora, ${horaEn(new Date(), contexto.zona)}`}</Chip>}
            </div>
            <div className="rv-rs-nota">
                <Icono nombre="eye" />
                <span>Con tu rol ves las cifras del día por franja, sin datos de los clientes.</span>
            </div>
            {vista === null ? (
                <EstadoCargando seccion="las reservas" />
            ) : vista.error !== null ? (
                <EstadoError seccion="las reservas" codigoSoporte={estadoDeError(vista.error).codigoSoporte} onReintentar={recargar} movil={movil} />
            ) : punto?.value == null ? (
                <div className="rv-ca-vacia">No hay reservas este día.</div>
            ) : (
                <>
                    <div className={`rv-rs-cifras${movil ? ' rv-rs-cifras--movil' : ''}`}>
                        <TarjetaCifra titulo="Ocupación" cifra={porcentaje(punto.value)} pie={punto.numerator == null || punto.denominator == null ? undefined : `${String(punto.numerator)} de ${String(punto.denominator)} plazas`} />
                    </div>
                    {porZona.length > 0 && (
                        <div className="rv-rs-lista-blanca" role="list" aria-label="Ocupación por zona">
                            {porZona.map((s) => (
                                <div key={s.dimension_value ?? ''} role="listitem" className="rv-rs-socio-fila">
                                    <strong>{s.label ?? contexto.zonas.find((z) => z.id === s.dimension_value)?.name ?? 'Zona'}</strong>
                                    <span>{porcentaje(s.total.value)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
